import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { signInWithToken, signOut, subscribeToAuthState, isFirebaseConfigured } from '../lib/firebase/auth';

interface AuthState {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  loginWithOTP: (email: string, otpCode: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAdmin: false,
    loading: true,
    error: null,
  });

  const checkAdminClaims = useCallback(async (user: User | null) => {
    if (!user) {
      setState(prev => ({ ...prev, user: null, isAdmin: false, loading: false }));
      return;
    }
    try {
      const tokenResult = await user.getIdTokenResult();
      const isAdminClaim = tokenResult?.claims?.admin === true;
      setState(prev => ({ ...prev, user, isAdmin: isAdminClaim, loading: false }));
    } catch {
      setState(prev => ({ ...prev, user, isAdmin: false, loading: false }));
    }
  }, []);

  useEffect(() => {
    if (!isFirebaseConfigured) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }
    const unsubscribe = subscribeToAuthState((user) => {
      checkAdminClaims(user);
    });
    return () => unsubscribe();
  }, [checkAdminClaims]);

  const loginWithOTP = useCallback(async (email: string, otpCode: string): Promise<boolean> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch('/api/admin/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: otpCode }),
      });
      const data = await response.json();

      if (!response.ok || !data.token) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: data.error || 'Invalid or expired code.',
        }));
        return false;
      }

      const result = await signInWithToken(data.token);
      if (result.error) {
        setState(prev => ({ ...prev, loading: false, error: result.error }));
        return false;
      }

      if (result.user) {
        const tokenResult = await result.user.getIdTokenResult();
        const isAdmin = tokenResult?.claims?.admin === true;
        if (!isAdmin) {
          await signOut();
          setState(prev => ({
            ...prev,
            user: null,
            isAdmin: false,
            loading: false,
            error: 'You do not have admin privileges.',
          }));
          return false;
        }
        setState(prev => ({ ...prev, user: result.user, isAdmin: true, loading: false }));
        return true;
      }
      return false;
    } catch {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'An error occurred during login.',
      }));
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      await signOut();
      setState({ user: null, isAdmin: false, loading: false, error: null });
    } catch {
      setState(prev => ({ ...prev, loading: false, error: 'Logout failed.' }));
    }
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, loginWithOTP, logout, clearError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
